// Immutable, group-scoped helpers for the audience predicate tree. Every edit returns a NEW node — no node
// mutates in place — so the whole `where` stays a controlled value the container owns (see PredicateTree).
// A group is one of the single-key arms `{ and: [...] }` / `{ or: [...] }`; these helpers read/rebuild it
// combinator-agnostically. Addressing stays path-based (updateAt / removeAt target by index).
//
// This slice only RENDERS + EDITS + REMOVES the tree it receives; adding nodes (and the nesting cap that would
// gate it) is deferred — so there are no insert/depth helpers yet.
//
// What lives here is EDIT logic: every helper below REBUILDS a node, which needs the concrete shape and is
// wanted only by the editing surface. The READ questions ("is this a group?", "what are its children?") are
// not builder-specific and live in `@server/audience-tree`.

import { combinatorChildrenOf, isGroupNode } from '@server/audience-tree';

import type { GroupNode, Predicate } from '../types';

/** Compile-time exhaustiveness guard — unreachable at runtime once every predicate arm is handled. */
export function assertNever(node: never): never {
  throw new Error(`Unhandled predicate node: ${JSON.stringify(node)}`);
}

/** Whether a node is an AND/OR group (vs. an attribute / rawSql leaf). */
export const isGroup = (node: Predicate): boolean => isGroupNode<Predicate>(node);

/** The children of a group, whichever arm it is. Non-null by construction: a `GroupNode` always has one
 *  of the two arms, which the shared accessor cannot know from its structural constraint alone. */
export const groupChildren = (group: GroupNode): Predicate[] => combinatorChildrenOf<Predicate>(group) ?? [];

/** A group's combinator. */
export const groupOp = (group: GroupNode): 'and' | 'or' => ('and' in group ? 'and' : 'or');

/** Rebuild a group with new children, preserving its combinator and `id`. */
const withChildren = (group: GroupNode, children: Predicate[]): GroupNode =>
  'and' in group ? { and: children, id: group.id } : { or: children, id: group.id };

/** Flip a group's AND/OR combinator (children + `id` preserved). */
export function setGroupOp(group: GroupNode, op: 'and' | 'or'): GroupNode {
  return op === 'and' ? { and: groupChildren(group), id: group.id } : { or: groupChildren(group), id: group.id };
}

/** Replace the child at `index`; an out-of-range index leaves the group unchanged. */
export function updateAt(group: GroupNode, index: number, next: Predicate): GroupNode {
  const children = groupChildren(group);
  if (index < 0 || index >= children.length) return group;
  return withChildren(
    group,
    children.map((child, i) => (i === index ? next : child)),
  );
}

/** Remove the child at `index`; an out-of-range index leaves the group unchanged. */
export function removeAt(group: GroupNode, index: number): GroupNode {
  const children = groupChildren(group);
  if (index < 0 || index >= children.length) return group;
  return withChildren(
    group,
    children.filter((_, i) => i !== index),
  );
}

/** Drop empty AND/OR groups bottom-up: a group left with no children is removed from its parent, cascading
 *  upward — so deleting the last entry in a block deletes the block. Returns `null` when the whole tree
 *  prunes away (the container maps that to no `where`). Leaves pass through unchanged. */
export function pruneEmptyGroups(node: Predicate): Predicate | null {
  if ('and' in node) {
    const children = node.and.map(pruneEmptyGroups).filter((c): c is Predicate => c !== null);
    return children.length > 0 ? { and: children, id: node.id } : null;
  }
  if ('or' in node) {
    const children = node.or.map(pruneEmptyGroups).filter((c): c is Predicate => c !== null);
    return children.length > 0 ? { or: children, id: node.id } : null;
  }
  return node;
}
